import { useQueryClient } from '@tanstack/react-query';
import { AxiosProgressEvent, AxiosResponse } from 'axios';

import { FileManagementResponse } from '../../../../../types/FileManagementResponse';
import * as offerFileManagementApi from '../../../../common/api/clients/offerFileManagement.api';
import * as offerInternalFileManagementApi from '../../../../common/api/clients/offerInternalFileManagement.api';
import * as supplierOnboardingApi from '../../../../common/api/clients/supplierOnboarding.api';
import { queries } from '../../../../common/api/queryKeys/queries';
import { Attachments } from '../../../../common/Attachments/components/Attachments';
import { useRole } from '../../../../common/hooks/useRole';
import { useOfferOnboarding } from '../../../../supplierOnboarding/hooks/useOfferOnboarding';
import {
    SUPPLIER_ONBOARDING_DOCUMENTS_FILE_ID,
    supplierOnboardingAttachmentFiles,
} from '../../../../supplierOnboarding/supplierOnboardingAttachmentFiles';
import { OfferModel } from '../../../model/OfferModel';

interface OfferDetailAttachmentsProps {
    offer: OfferModel;
}

export const OfferDetailAttachments = ({ offer }: OfferDetailAttachmentsProps) => {
    const queryClient = useQueryClient();
    const role = useRole();
    const onboarding = useOfferOnboarding(offer);

    const files = [...offer.files, ...supplierOnboardingAttachmentFiles(onboarding, offer)];

    const uploadFile = (
        file: File,
        onUploadProgress: (event: AxiosProgressEvent) => void
    ): Promise<FileManagementResponse> => {
        return offerFileManagementApi.uploadFile(file, offer.id, onUploadProgress);
    };

    const deleteFile = (fileId: string): Promise<void> => {
        return offerFileManagementApi.deleteFile([fileId], offer.id);
    };

    const downloadFile = (fileId: string): Promise<AxiosResponse> => {
        if (fileId === SUPPLIER_ONBOARDING_DOCUMENTS_FILE_ID) {
            return supplierOnboardingApi.downloadSupplierDocuments(offer.id);
        }
        return offerFileManagementApi.downloadFile(fileId, offer.id);
    };

    const uploadInternalFile = (
        file: File,
        onUploadProgress: (event: AxiosProgressEvent) => void
    ): Promise<FileManagementResponse> => {
        return offerInternalFileManagementApi.uploadInternalFile(file, offer.id, onUploadProgress);
    };

    const deleteInternalFile = (fileId: string): Promise<void> => {
        return offerInternalFileManagementApi.deleteInternalFile(fileId, offer.id);
    };

    const downloadInternalFile = (fileId: string): Promise<AxiosResponse> => {
        return offerInternalFileManagementApi.downloadInternalFile(fileId, offer.id);
    };

    const invalidateCache = async () => {
        await queryClient.cancelQueries({ queryKey: queries.offers.detail(offer.id).queryKey });
        return queryClient.invalidateQueries({ queryKey: queries.offers.detail(offer.id).queryKey });
    };

    if (role.isSuperAdmin() || offer.isCanceled()) {
        return (
            <Attachments
                files={files}
                internalFiles={offer.internalFiles}
                config={{
                    uploadFile,
                    deleteFile,
                    downloadFile,
                    uploadInternalFile,
                    deleteInternalFile,
                    downloadInternalFile,
                    invalidateCache,
                    hasWriteRights: false,
                    enableDownloadUploadedFiles: true,
                }}
            />
        );
    }

    const companyId = role.isCustomer() ? offer.customerId : offer.supplierId;
    const counterCompanyName = role.isCustomer() ? offer.supplier.name : offer.customer.name;

    return (
        <Attachments
            files={offer.files}
            internalFiles={offer.internalFiles}
            companyId={companyId}
            counterCompanyName={counterCompanyName}
            config={{
                uploadFile,
                deleteFile,
                downloadFile,
                uploadInternalFile,
                deleteInternalFile,
                downloadInternalFile,
                invalidateCache,
                hasWriteRights: role.hasWriteRights(companyId) && !offer.isCanceled(),
                enableDownloadUploadedFiles: true,
            }}
        />
    );
};
